import { Controller } from "@hotwired/stimulus"

// One live form, still in its Stimulus scope (the page shell, which holds both the header and the
// scroller). It follows its anchor up the page, then clamps in the header's slot. Scroll position
// drives both directions.
//
// While floating, the form is a child of the header, not of the scroller. On iPhone Safari a
// position: fixed form left inside the scroller was cut off at the scroller's top edge, so it slid
// under the header and disappeared (desktop engines don't clip it).
export default class extends Controller {
  static targets = ["scroller", "anchor", "form"]

  connect() {
    this.header = document.querySelector(".search-v2-header")
    this.nav = this.header?.querySelector(".search-v2-header__nav--search")
    this.slot = this.nav?.querySelector(".search-v2-header__search-slot")
    if (!this.slot || !this.hasScrollerTarget || !this.hasAnchorTarget || !this.hasFormTarget) {
      this.slot = null
      return
    }

    this.scroller = this.scrollerTarget
    this.anchor = this.anchorTarget
    this.form = this.formTarget
    this.brand = this.nav.querySelector(".search-v2-header__brand")
    this.actions = this.nav.querySelector(".search-v2-header__actions")
    this.observer = new ResizeObserver(() => this.resize())
    for (const element of [this.header, this.brand, this.actions, this.anchor, this.form]) this.observer.observe(element)
    this.resize()
  }

  resize() {
    if (!this.slot || !this.scroller.getClientRects().length) return
    const height = this.form.offsetHeight
    this.anchor.style.minHeight = `${height}px`
    this.nav.style.setProperty("--search-form-height", `${height}px`)

    // Equal grid wings keep the center on the page, not halfway between unequal logo/actions.
    const width = this.nav.clientWidth
    const actionWidth = this.actions.offsetWidth
    const sideWidth = Math.max(this.brand.offsetWidth, actionWidth)
    this.compact = width - 2 * (sideWidth + 24) < 320
    const gap = this.compact ? 12 : 24
    const wing = this.compact ? actionWidth : sideWidth
    this.nav.style.setProperty("--search-dock-width", `${Math.max(0, Math.min(640, width - 2 * (wing + gap)))}px`)
    this.nav.style.setProperty("--search-dock-gap", `${gap}px`)
    this.update()
  }

  update() {
    if (!this.slot) return
    cancelAnimationFrame(this.frame)
    this.frame = requestAnimationFrame(() => this.measure())
  }

  measure() {
    if (!this.scroller.getClientRects().length) return
    const anchor = this.anchor.getBoundingClientRect()
    const slot = this.slot.getBoundingClientRect()
    const edge = this.scroller.getBoundingClientRect().top
    const handoffStart = edge + slot.height
    const floating = anchor.top < handoffStart
    this.header.classList.toggle("search-v2-header--search-compact", anchor.top < slot.bottom && this.compact)
    if (!floating) {
      this.clearPosition()
      return
    }

    // Enter fixed positioning at the exact same coordinates as the form in the page.
    // Finish narrowing before entering the nav row, so History never overlaps the input.
    // The same scroll-linked path in reverse leaves the form in its original page position.
    const progress = Math.min(1, (handoffStart - anchor.top) / Math.max(1, handoffStart - slot.bottom))
    const width = anchor.width + (slot.width - anchor.width) * progress
    const center = anchor.left + anchor.width / 2
    this.form.classList.add("provider-landing__search-docked")
    Object.assign(this.form.style, {
      top: `${Math.max(anchor.top, slot.top)}px`,
      left: `${center - width / 2}px`,
      width: `${width}px`
    })
    this.moveFormTo(this.header)
  }

  clearPosition() {
    if (!this.form) return
    this.moveFormTo(this.anchor)
    this.form.classList.remove("provider-landing__search-docked")
    for (const property of ["left", "top", "width"]) this.form.style.removeProperty(property)
  }

  // Moving a node drops focus. If a member is typing while the form crosses the handoff, refocus the
  // same input (its value, typeahead and hidden fields move with it) and keep the caret where it was.
  moveFormTo(parent) {
    if (this.form.parentElement === parent) return
    const focused = this.form.contains(document.activeElement) ? document.activeElement : null
    const selection = focused?.selectionStart == null ? null : [focused.selectionStart, focused.selectionEnd]
    parent.append(this.form)
    if (!focused) return
    focused.focus({ preventScroll: true })
    if (selection) focused.setSelectionRange(...selection)
  }

  reset() {
    cancelAnimationFrame(this.frame)
    this.header?.classList.remove("search-v2-header--search-compact")
    this.clearPosition()
  }

  disconnect() {
    this.observer?.disconnect()
    this.reset()
  }
}
